<script lang="ts">
  import { onMount, tick } from 'svelte';
  import type { TourStep } from '../lib/onboarding';
  import { markOnboardingDone } from '../lib/onboarding';

  let {
    open = $bindable(false),
    steps,
    tenantId,
    onNavigate,
    onClose,
  }: {
    open?: boolean;
    steps: TourStep[];
    tenantId: string;
    onNavigate: (tab: string) => void;
    onClose?: () => void;
  } = $props();

  let idx = $state(0);
  let rect = $state<DOMRect | null>(null);
  let tooltipStyle = $state('');

  const step = $derived(steps[idx] ?? null);
  const isLast = $derived(idx >= steps.length - 1);
  const isFirst = $derived(idx === 0);
  const progress = $derived(steps.length ? ((idx + 1) / steps.length) * 100 : 0);

  async function measureTarget() {
    await tick();
    await new Promise(r => requestAnimationFrame(r));
    const s = steps[idx];
    if (!s?.target) {
      rect = null;
      tooltipStyle = 'top:50%;left:50%;transform:translate(-50%,-50%);max-width:420px;';
      return;
    }
    const el = document.querySelector(`[data-tour="${s.target}"]`);
    if (!el) {
      rect = null;
      tooltipStyle = 'top:50%;left:50%;transform:translate(-50%,-50%);max-width:420px;';
      return;
    }
    el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    await new Promise(r => setTimeout(r, 280));
    const r = el.getBoundingClientRect();
    rect = r;
    const pad = 8;
    const top = r.bottom + pad + 12;
    const fitsBelow = top + 220 < window.innerHeight;
    if (fitsBelow) {
      tooltipStyle = `top:${top}px;left:${Math.max(16, Math.min(r.left, window.innerWidth - 436))}px;max-width:420px;`;
    } else {
      tooltipStyle = `bottom:${window.innerHeight - r.top + pad + 12}px;left:${Math.max(16, Math.min(r.left, window.innerWidth - 436))}px;max-width:420px;`;
    }
  }

  async function goTo(i: number) {
    const s = steps[i];
    if (s?.tab) onNavigate(s.tab);
    idx = i;
    await measureTarget();
  }

  async function next() {
    if (isLast) finish();
    else await goTo(idx + 1);
  }

  async function prev() {
    if (idx > 0) await goTo(idx - 1);
  }

  function finish() {
    markOnboardingDone(tenantId);
    open = false;
    onClose?.();
  }

  function skip() {
    markOnboardingDone(tenantId);
    open = false;
    onClose?.();
  }

  $effect(() => {
    if (open && steps.length) {
      idx = 0;
      void (async () => {
        if (steps[0]?.tab) onNavigate(steps[0].tab);
        await measureTarget();
      })();
    }
  });

  onMount(() => {
    const onResize = () => { if (open) void measureTarget(); };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  });
</script>

{#if open && step}
  <div class="tour-root" role="dialog" aria-modal="true" aria-labelledby="tour-title">
    <button type="button" class="backdrop" aria-label="Omitir guía" onclick={skip}></button>

    {#if rect}
      <div
        class="spotlight"
        style="top:{rect.top - 6}px;left:{rect.left - 6}px;width:{rect.width + 12}px;height:{rect.height + 12}px;"
      ></div>
    {/if}

    <div class="tooltip" style={tooltipStyle}>
      <div class="progress"><div class="bar" style="width:{progress}%;animation:barPulse 2s ease infinite"></div></div>
      <div class="head">
        {#if step.icon}<span class="ico" style="animation:iconBounce 0.6s ease">{step.icon}</span>{/if}
        <div>
          <p class="step-num">Paso {idx + 1} de {steps.length}</p>
          <h2 id="tour-title">{step.title}</h2>
        </div>
      </div>
      <p class="body">{step.body}</p>
      <div class="actions">
        <button type="button" class="ghost" onclick={skip}>Omitir recorrido</button>
        <div class="nav-btns">
          {#if !isFirst}
            <button type="button" class="secondary" onclick={prev}>← Anterior</button>
          {/if}
          <button type="button" class="primary" onclick={next}>
            {isLast ? '✨ Comenzar' : 'Siguiente →'}
          </button>
        </div>
      </div>
    </div>
  </div>
{/if}

<style>
  .tour-root {
    position: fixed;
    inset: 0;
    z-index: 10000;
    pointer-events: none;
  }
  .backdrop {
    position: absolute;
    inset: 0;
    border: 0;
    background: rgb(15 23 42 / 65%);
    backdrop-filter: blur(4px);
    -webkit-backdrop-filter: blur(4px);
    cursor: pointer;
    pointer-events: auto;
    transition: background 0.3s;
  }
  .spotlight {
    position: fixed;
    border-radius: 14px;
    box-shadow: 0 0 0 9999px rgb(15 23 42 / 65%);
    pointer-events: none;
    z-index: 10001;
    transition: top 0.35s cubic-bezier(0.34, 1.56, 0.64, 1), left 0.35s cubic-bezier(0.34, 1.56, 0.64, 1), width 0.35s ease, height 0.35s ease;
    outline: 3px solid rgba(255,255,255,0.3);
    outline-offset: 4px;
  }
  .tooltip {
    position: fixed;
    z-index: 10002;
    background: rgba(255,255,255,0.92);
    backdrop-filter: blur(30px) saturate(180%);
    -webkit-backdrop-filter: blur(30px) saturate(180%);
    border: 1px solid rgba(255,255,255,0.5);
    border-radius: 20px;
    padding: 1.25rem 1.35rem 1.1rem;
    box-shadow: 0 25px 60px rgb(15 23 42 / 28%), 0 0 0 1px rgba(255,255,255,0.5);
    pointer-events: auto;
    animation: popIn 0.35s cubic-bezier(0.34, 1.56, 0.64, 1);
    transition: top 0.35s ease, left 0.35s ease;
  }
  @keyframes popIn {
    from { opacity: 0; transform: translateY(16px) scale(0.95); }
    to { opacity: 1; transform: translateY(0) scale(1); }
  }
  @keyframes iconBounce {
    0% { transform: scale(0.5); opacity: 0; }
    60% { transform: scale(1.15); }
    100% { transform: scale(1); opacity: 1; }
  }
  @keyframes barPulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.8; }
  }
  .progress {
    height: 5px;
    background: rgba(15,23,42,0.06);
    border-radius: 999px;
    margin-bottom: 0.9rem;
    overflow: hidden;
  }
  .bar {
    height: 100%;
    background: linear-gradient(90deg, var(--brand-strong, #0d9488), var(--brand, #14b8a6), var(--brand-strong, #0d9488));
    background-size: 200% 100%;
    border-radius: 999px;
    transition: width 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
    animation: barShift 2s linear infinite;
  }
  @keyframes barShift {
    0% { background-position: 0% 0%; }
    100% { background-position: 200% 0%; }
  }
  .head {
    display: flex;
    gap: 0.75rem;
    align-items: flex-start;
    margin-bottom: 0.7rem;
  }
  .ico { font-size: 2rem; line-height: 1; filter: drop-shadow(0 2px 4px rgba(0,0,0,0.1)); }
  .step-num {
    margin: 0;
    font-size: 0.65rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: var(--text-secondary, #64748b);
  }
  h2 {
    margin: 0.1rem 0 0;
    font-family: Poppins, Inter, system-ui, sans-serif;
    font-size: 1.2rem;
    font-weight: 800;
    color: var(--text-primary, #0f172a);
    letter-spacing: -0.02em;
  }
  .body {
    margin: 0 0 1.1rem;
    font-size: 0.9rem;
    line-height: 1.6;
    color: var(--text-secondary, #475569);
  }
  .actions {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    flex-wrap: wrap;
  }
  .nav-btns { display: flex; gap: 0.5rem; margin-left: auto; }
  .ghost {
    border: 0;
    background: transparent;
    color: var(--text-secondary, #94a3b8);
    font: inherit;
    font-size: 0.8rem;
    cursor: pointer;
    padding: 0.35rem 0.5rem;
    transition: color 0.2s;
  }
  .ghost:hover { color: var(--text-primary); }
  .secondary, .primary {
    border: 0;
    border-radius: 12px;
    font: inherit;
    font-size: 0.88rem;
    font-weight: 700;
    cursor: pointer;
    padding: 0.6rem 1rem;
    transition: all 0.25s cubic-bezier(0.34, 1.56, 0.64, 1);
  }
  .secondary {
    background: rgba(15,23,42,0.04);
    color: var(--text-primary);
    border: 1px solid rgba(15,23,42,0.08);
  }
  .secondary:hover { background: rgba(15,23,42,0.08); transform: translateY(-1px); }
  .primary {
    background: linear-gradient(135deg, var(--brand, #0d9488), var(--brand-deep, #0f766e));
    color: #fff;
    box-shadow: 0 6px 20px color-mix(in srgb, var(--brand, #0d9488) 40%, transparent);
  }
  .primary:hover { transform: translateY(-2px); box-shadow: 0 10px 28px color-mix(in srgb, var(--brand, #0d9488) 50%, transparent); }
</style>
